import {useCallback, useMemo} from 'react';
import PropTypes from 'prop-types';
import {FormattedMessage, useIntl} from 'react-intl';

import {
  Box,
  Flex,
  SimpleGrid,
  Skeleton,
  Spinner,
  Text,
  useBreakpointValue,
} from '@salesforce/retail-react-app/app/components/shared/ui';

import {isCushionSingleTitleCustomizeStep} from '@amblique/king-living/app/hooks/use-customize-steps';
import {getProductEffectivePrice, getProductListPrice, sfccProductIdDataAttrs} from '@amblique/king-living/app/utils/product-utils';
import {getInventory, INVENTORY_STATUS} from '@amblique/king-living/app/utils/inventory-utils';
import {useShopperInventoryIds} from '@amblique/king-living/app/hooks/use-shopper-inventory-ids';
import {useProductSlotData} from '@amblique/king-living/app/hooks/use-product-slot-data';
import {buildOptionSlotSelectionFromProduct} from '@amblique/king-living/app/utils/customize-selections';
import {RadioOptionContent} from './radio-option-content';
import {SwatchOptionCard} from './swatch-option-card';
import {Markup} from '@amblique/base/app/components/shared/markup';

import {applyKnownBlockInfoTranslations, wrapBlockInfoText} from '../../../utils/wrap-block-info-text';
import {handleRadioTileKeyDown} from '@amblique/king-living/app/utils/radio-keyboard';

/**
 * Single-slot inline radio layout (container with one recommendationTypeId).
 * No sub-view; selection confirmed immediately on tap.
 * Products fetched eagerly when the step is visible.
 *
 * Derives visible product IDs from value.products (isHidden=false) and
 * auto-selects the first product with isPreselected=true if no selection exists.
 */
export function OptionStepRadio({
  blockInfo,
  container,
  customizeStepKind = 'other',
  isSimplePDP = false,
  isStepDisabled,
  onSelect,
  onSlotProductPreview,
  onStepInteraction,
  onViewDetails,
  selections = [],
  stepId,
  styles,
}) {
  const intl = useIntl();
  const isMobile = useBreakpointValue({base: true, lg: false});
  const shopperInventoryIds = useShopperInventoryIds();
  const hasValues = Boolean(container?.values?.length);
  const value = hasValues ? container.values[0] : null;
  const currentSelection = selections[0] ?? null;

  const handleAutoSelect = useCallback(
    (product) => {
      onSelect(container.containerId, [
        buildOptionSlotSelectionFromProduct(product, 0, value, container)
      ]);
    },
    [onSelect, container, value]
  );

  // Suppress preselection inside Simple PDP option containers (FSD §1.5, §4.1
  // — "no option will be pre-selected by default"). Cover step keeps its own auto-select
  // (handled by CoverSwatchGrid / useFreeformCoverPreselect / CoverStep, not this hook).
  const {products, isLoading, isLoadingMore} = useProductSlotData(value, currentSelection, handleAutoSelect, {
    disablePreselect: isSimplePDP,
  });

  // Swatch: all recs no extra price on Simple PDP. Radio: any extra price > 0.
  const useSwatchLayout = useMemo(() => {
    if (!isSimplePDP) return false;
    if (!products.length) return false;

    const requiresRadioLayout = products.some((p) => {
      const extra = getProductEffectivePrice(p);

      return typeof extra === 'number' && extra > 0;
    });

    // Wait for all chunks before swatch — partial load can flip swatch→radio mid-stream.
    return !requiresRadioLayout && !isLoadingMore;
  }, [isSimplePDP, products, isLoadingMore]);

  const handleSelect = useCallback(
    (product) => {
      // OOS guard: never commit an out-of-stock product.
      if (getInventory(product, undefined, shopperInventoryIds).status === INVENTORY_STATUS.OUT_OF_STOCK) {
        return;
      }

      if (onStepInteraction && stepId) {
        onStepInteraction(stepId);
      }

      if (
        currentSelection?.productId != null &&
        String(currentSelection.productId) === String(product.id)
      ) {
        onSelect(container.containerId, [null]);
        onSlotProductPreview?.(null);

        return;
      }

      // Use the canonical builder so cylindoFeature + listPrice + pimImages flow into
      // committed selections — required for layered V2 gallery (FSD §4.2) and total/list price.
      onSelect(container.containerId, [
        buildOptionSlotSelectionFromProduct(product, 0, value, container)
      ]);

      onSlotProductPreview?.(product);
    },
    [
      onStepInteraction,
      stepId,
      onSelect,
      container,
      value,
      onSlotProductPreview,
      shopperInventoryIds,
      currentSelection?.productId
    ]
  );

  if (!hasValues) {
    return null;
  }

  // KINDW-1119 §B2 — per-item "See details" trigger (Simple PDP / mobile only).
  // Rendered below each option card/row. Clicking opens the details overlay for THAT
  // specific product (independent of current selection).
  const perItemViewDetails =
    isSimplePDP && isMobile && onViewDetails && !isLoadingMore ? onViewDetails : undefined;

  return (
    <Box
      data-customize-step-kind={customizeStepKind}
      {...styles.optionStepRadioContainer}
      className={[
        'option-step-radio-container',
        'customize-steps-option-step',
        'customize-steps-option-step-radio',
        `customize-steps-option-step--${customizeStepKind}`,
        isSimplePDP && 'option-step-radio-container--simple-pdp',
        isCushionSingleTitleCustomizeStep({container, id: stepId}) && 'customize-steps-option-step--cushion'
      ].filter(Boolean).join(' ')}
    >
      {blockInfo && (
        <Box className="step-information customize-steps-step-information" {...styles.stepDescription}>
          <Box {...styles.stepDescriptionBody}>
            <Markup source={wrapBlockInfoText(applyKnownBlockInfoTranslations(blockInfo, intl))} />
          </Box>
        </Box>
      )}
      {isLoading ? (
        <Box>
          <Skeleton height="72px" marginBottom={2} />
          <Skeleton height="72px" marginBottom={2} />
          <Skeleton height="72px" />
        </Box>
      ) : products.length > 0 ? (
        <Box aria-busy={isLoadingMore} className="option-step-radio-list-wrapper">
          {useSwatchLayout ? (
            <SimpleGrid
              {...styles.optionStepListSwatch}
              {...(isLoadingMore ? styles.optionStepListLocked : {})}
              className="option-step-list option-step-list__swatch"
            >
              {products.map((product) => {
                const isSelected = currentSelection?.productId === product.id;
                const isOutOfStock = getInventory(product, undefined, shopperInventoryIds).status === INVENTORY_STATUS.OUT_OF_STOCK;

                return (
                  <SwatchOptionCard
                    key={product.id}
                    isDisabled={isStepDisabled || isOutOfStock || isLoadingMore}
                    isOutOfStock={isOutOfStock}
                    isSelected={isSelected}
                    product={product}
                    onSelect={handleSelect}
                    onViewDetails={perItemViewDetails}
                  />
                );
              })}
            </SimpleGrid>
          ) : (
            <Box
              role="radiogroup"
              {...styles.optionStepListRadio}
              {...(isLoadingMore ? styles.optionStepListLocked : {})}
              className={[
                'option-step-list',
                'option-step-list__radio',
                isSimplePDP && 'option-step-list__radio--simple-pdp'
              ].filter(Boolean).join(' ')}
            >
              {products.map((product) => {
                const isSelected = currentSelection?.productId === product.id;
                const extraPrice = getProductEffectivePrice(product);
                const productListPrice = getProductListPrice(product);
                const isOutOfStock =
                  getInventory(product, undefined, shopperInventoryIds).status === INVENTORY_STATUS.OUT_OF_STOCK;
                const isRowDisabled = isStepDisabled || isOutOfStock || isLoadingMore;

                return (
                  <Box
                    key={product.id}
                    aria-checked={isSelected}
                    aria-disabled={isRowDisabled}
                    marginBottom={isSimplePDP ? 0 : 2}
                    opacity={isOutOfStock ? 0.5 : 1}
                    pointerEvents={isRowDisabled ? 'none' : 'auto'}
                    role="radio"
                    tabIndex={isRowDisabled ? -1 : 0}
                    {...styles.radioOption}
                    {...(isSelected ? styles.radioOptionSelected : {})}
                    className={[
                      'option-inner-radio-item',
                      isSelected && 'option-inner-radio-item--selected'
                    ].filter(Boolean).join(' ')}
                    {...sfccProductIdDataAttrs('option', product)}
                    onClick={() => !isRowDisabled && handleSelect(product)}
                    onKeyDown={(event) => handleRadioTileKeyDown(event, isRowDisabled, () => handleSelect(product))}
                  >
                    <RadioOptionContent
                      containerId={container.containerId}
                      customizeStepKind={customizeStepKind}
                      extraPrice={extraPrice > 0 ? extraPrice : undefined}
                      isOutOfStock={isOutOfStock}
                      isSelected={isSelected}
                      listPrice={productListPrice}
                      product={product}
                      styles={styles}
                      useListLayout={isSimplePDP}
                      useSingleTypeTitleLayout={isCushionSingleTitleCustomizeStep({
                        container,
                        id: stepId,
                      })}
                      onViewDetails={perItemViewDetails}
                    />
                  </Box>
                );
              })}
            </Box>
          )}
          {isLoadingMore && (
            <Flex
              aria-live="polite"
              as="div"
              className="option-step-radio-loading-more"
              {...styles.optionStepLoadingMore}
            >
              <Spinner {...styles.optionStepLoadingMoreSpinner} />
              <Text {...styles.optionStepLoadingMoreText}>
                <FormattedMessage
                  defaultMessage="Loading more"
                  id="customize_panel.option_step.loading_more"
                />
              </Text>
            </Flex>
          )}
        </Box>
      ) : null}
    </Box>
  );
}

OptionStepRadio.displayName = 'OptionStepRadio';

OptionStepRadio.propTypes = {
  blockInfo: PropTypes.string,
  customizeStepKind: PropTypes.string,
  container: PropTypes.shape({
    containerId: PropTypes.string.isRequired,
    containerName: PropTypes.string,
    isVisible: PropTypes.bool,
    values: PropTypes.arrayOf(
      PropTypes.shape({
        isRequired: PropTypes.bool,
        isVisible: PropTypes.bool,
        products: PropTypes.arrayOf(
          PropTypes.shape({
            isHidden: PropTypes.bool,
            isPreselected: PropTypes.bool,
            productId: PropTypes.string.isRequired,
          })
        ),
        recommendationTypeId: PropTypes.number,
        typeName: PropTypes.string,
      })
    ).isRequired,
  }).isRequired,
  isSimplePDP: PropTypes.bool,
  isStepDisabled: PropTypes.bool,
  onSelect: PropTypes.func.isRequired,
  onSlotProductPreview: PropTypes.func,
  onStepInteraction: PropTypes.func,
  onViewDetails: PropTypes.func,
  selections: PropTypes.array,
  stepId: PropTypes.string,
  styles: PropTypes.object,
};
